import React, { useState, useEffect, useMemo } from 'react';
import {
    collection, query, where, getDocs, doc, getDoc
} from 'firebase/firestore';
import { db } from '../firebase/config';
import {
    BarChart, Bar, PieChart, Pie, Cell,
    XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
    LineChart, Line, Area, AreaChart
} from 'recharts';
import {
    TrendingUp, TrendingDown, Package, AlertTriangle,
    DollarSign, RefreshCw, Download,
    Activity, Award, Users, Calendar,
    FileText, Clock, CheckCircle
} from 'lucide-react';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import { ko } from 'date-fns/locale';
import './Statistics.css';

// 유틸: Firestore Timestamp → Date 변환
const toDate = (ts) => {
    if (!ts) return null;
    if (ts.toDate) return ts.toDate();
    if (ts instanceof Date) return ts;
    return new Date(ts);
};

function Statistics({ user }) {
    // 상태 관리
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('trade');
    const [dateRange, setDateRange] = useState({
        startDate: new Date(new Date().setMonth(new Date().getMonth() - 3)),
        endDate: new Date()
    });
    const [selectedPeriod, setSelectedPeriod] = useState('month');
    const [selectedPartner, setSelectedPartner] = useState('all');

    // 데이터
    const [statements, setStatements] = useState([]);
    const [workOrders, setWorkOrders] = useState([]);
    const [partners, setPartners] = useState([]);

    // 데이터 로드
    useEffect(() => {
        if (user) {
            loadAllData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user, dateRange.startDate, dateRange.endDate]);

    const loadAllData = async () => {
        setLoading(true);
        try {
            await Promise.all([
                loadPartners(),
                loadStatements(),
                loadOrders()
            ]);
        } catch (error) {
            console.error('데이터 로드 실패:', error);
            alert('데이터를 불러오는데 실패했습니다: ' + error.message);
        } finally {
            setLoading(false);
        }
    };

    // 거래처 목록 로드
    const loadPartners = async () => {
        try {
            const partnersMap = new Map();
            // 2026-09-10 보안 수정: connections 읽기 rules 가 당사자 한정으로 바뀌어 status 만 거는 전체 스캔은 거부된다.
            //   내 id(회사 기준)가 requesterId / receiverId / recipientId 인 accepted 연결만 3갈래로 조회해 합친다.
            //   (아래 루프의 방향 판정은 그대로 — 결과 집합만 좁아진다)
            const myId = user?.companyId || user?.uid;
            const connectionsRef = collection(db, 'connections');
            const [sentSnap, recvSnap, recipSnap] = await Promise.all([
                getDocs(query(connectionsRef, where('requesterId', '==', myId), where('status', '==', 'accepted'))),
                getDocs(query(connectionsRef, where('receiverId', '==', myId), where('status', '==', 'accepted'))),
                getDocs(query(connectionsRef, where('recipientId', '==', myId), where('status', '==', 'accepted'))),
            ]);
            const seenConn = new Set();
            const connectionsSnapshot = {
                docs: [...sentSnap.docs, ...recvSnap.docs, ...recipSnap.docs].filter(d => {
                    if (seenConn.has(d.id)) return false;
                    seenConn.add(d.id);
                    return true;
                }),
            };

            for (const docSnap of connectionsSnapshot.docs) {
                const connection = docSnap.data();

                if (connection.requesterId === (user?.companyId || user?.uid) && connection.receiverId) {
                    try {
                        const userDoc = await getDoc(doc(db, 'users', connection.receiverId));
                        if (userDoc.exists()) {
                            const userData = userDoc.data();
                            partnersMap.set(connection.receiverId, {
                                id: connection.receiverId,
                                name: userData.businessName || userData.companyName || userData.name || '이름 없음'
                            });
                        }
                    } catch (error) {
                        console.error('파트너 정보 조회 실패:', error);
                    }
                }

                if (connection.receiverId === (user?.companyId || user?.uid) && connection.requesterId) {
                    try {
                        const userDoc = await getDoc(doc(db, 'users', connection.requesterId));
                        if (userDoc.exists()) {
                            const userData = userDoc.data();
                            partnersMap.set(connection.requesterId, {
                                id: connection.requesterId,
                                name: userData.businessName || userData.companyName || userData.name || '이름 없음'
                            });
                        }
                    } catch (error) {
                        console.error('파트너 정보 조회 실패:', error);
                    }
                }
            }

            setPartners(Array.from(partnersMap.values()));
        } catch (error) {
            console.error('거래처 로드 실패:', error);
        }
    };

    // 거래명세서 데이터 로드
    const loadStatements = async () => {
        try {
            const statementsRef = collection(db, 'transactionStatements');
            const sentQuery = query(statementsRef, where('fromUserId', '==', (user?.companyId || user?.uid)));
            const receivedQuery = query(statementsRef, where('toUserId', '==', (user?.companyId || user?.uid)));

            const [sentSnapshot, receivedSnapshot] = await Promise.all([
                getDocs(sentQuery),
                getDocs(receivedQuery)
            ]);

            const allStatements = [
                ...sentSnapshot.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data(),
                    type: 'sent'
                })),
                ...receivedSnapshot.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data(),
                    type: 'received'
                }))
            ];

            const filteredStatements = allStatements.filter(statement => {
                // 발행처가 삭제·정정한 명세서는 통계 제외 (2026-10-23 검토 회의 — 정정본과 이중 합산 방지)
                if (statement.deletedBySender) return false;
                if (!statement.createdAt) return false;
                const statementDate = statement.createdAt.toDate ?
                    statement.createdAt.toDate() :
                    new Date(statement.createdAt);
                return statementDate >= dateRange.startDate && statementDate <= dateRange.endDate;
            });

            filteredStatements.sort((a, b) => {
                const dateA = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(0);
                const dateB = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(0);
                return dateB - dateA;
            });

            setStatements(filteredStatements);
        } catch (error) {
            console.error('거래명세서 로드 실패:', error);
        }
    };

    // 의뢰서(workOrders) 데이터 로드 - 납기 관리 탭에서 사용
    const loadOrders = async () => {
        try {
            const ordersRef = collection(db, 'workOrders');
            const sentQuery = query(ordersRef, where('fromUserId', '==', (user?.companyId || user?.uid)));
            const receivedQuery = query(ordersRef, where('toUserId', '==', (user?.companyId || user?.uid)));

            const [sentSnapshot, receivedSnapshot] = await Promise.all([
                getDocs(sentQuery),
                getDocs(receivedQuery)
            ]);

            const allOrders = [
                ...sentSnapshot.docs.map(d => ({
                    id: d.id,
                    ...d.data(),
                    direction: 'sent'
                })),
                ...receivedSnapshot.docs.map(d => ({
                    id: d.id,
                    ...d.data(),
                    direction: 'received'
                }))
            ];

            // 중복 제거 (본인에게 보낸 경우)
            const uniqueOrders = Array.from(
                new Map(allOrders.map(o => [o.id, o])).values()
            );

            // type === 'message' 제외
            const orders = uniqueOrders.filter(o => o.type !== 'message');

            // 날짜 필터링
            const filtered = orders.filter(order => {
                const date = toDate(order.createdAt);
                if (!date) return false;
                return date >= dateRange.startDate && date <= dateRange.endDate;
            });

            filtered.sort((a, b) => {
                const dateA = toDate(a.createdAt) || new Date(0);
                const dateB = toDate(b.createdAt) || new Date(0);
                return dateB - dateA;
            });

            setWorkOrders(filtered);
        } catch (error) {
            console.error('의뢰서 로드 실패:', error);
        }
    };

    // ================================================
    // 공통 필터링
    // ================================================

    const filteredStatements = useMemo(() => {
        if (selectedPartner === 'all') return statements;
        return statements.filter(statement => {
            const partnerId = statement.type === 'sent' ? statement.toUserId : statement.fromUserId;
            return partnerId === selectedPartner;
        });
    }, [statements, selectedPartner]);

    const filteredOrders = useMemo(() => {
        if (selectedPartner === 'all') return workOrders;
        return workOrders.filter(order => {
            const partnerId = order.direction === 'sent' ? order.toUserId : order.fromUserId;
            return partnerId === selectedPartner;
        });
    }, [workOrders, selectedPartner]);

    // ================================================
    // 탭 1: 거래 통계 (transactionStatements 기반)
    // ================================================

    const tradeStats = useMemo(() => {
        const totalStatements = filteredStatements.length;
        const totalTeeth = filteredStatements.reduce((sum, s) => sum + (s.totalTeeth || 0), 0);
        const totalRevenue = filteredStatements.reduce((sum, s) => sum + (s.totalAmount || 0), 0);

        const partnerIds = new Set();
        filteredStatements.forEach(s => {
            const pid = s.type === 'sent' ? s.toUserId : s.fromUserId;
            if (pid) partnerIds.add(pid);
        });

        const days = Math.max(1, Math.ceil((dateRange.endDate - dateRange.startDate) / (1000 * 60 * 60 * 24)));
        const dailyAvg = parseFloat((totalStatements / days).toFixed(1));

        // 전 기간 대비 증감률
        const periodLength = dateRange.endDate - dateRange.startDate;
        const previousStart = new Date(dateRange.startDate.getTime() - periodLength);
        const previousStatements = statements.filter(s => {
            if (!s.createdAt) return false;
            const date = s.createdAt.toDate ? s.createdAt.toDate() : new Date(s.createdAt);
            return date >= previousStart && date < dateRange.startDate;
        });
        const previousCount = previousStatements.length;
        const previousRevenue = previousStatements.reduce((sum, s) => sum + (s.totalAmount || 0), 0);

        const countGrowth = previousCount > 0
            ? parseFloat(((totalStatements - previousCount) / previousCount * 100).toFixed(1))
            : 0;
        const revenueGrowth = previousRevenue > 0
            ? parseFloat(((totalRevenue - previousRevenue) / previousRevenue * 100).toFixed(1))
            : 0;

        const avgAmount = totalStatements > 0 ? totalRevenue / totalStatements : 0;

        return {
            totalStatements,
            totalTeeth,
            totalRevenue,
            activePartners: partnerIds.size,
            dailyAvg,
            countGrowth,
            revenueGrowth,
            avgAmount
        };
    }, [filteredStatements, statements, dateRange]);

    // 기간별 매출/건수/치아 추이
    const tradePeriodData = useMemo(() => {
        const data = {};
        filteredStatements.forEach(statement => {
            const date = statement.createdAt?.toDate ? statement.createdAt.toDate() : new Date(statement.createdAt || Date.now());
            let key;
            switch (selectedPeriod) {
                case 'day':
                    key = date.toLocaleDateString('ko-KR');
                    break;
                case 'week': {
                    const weekStart = new Date(date);
                    weekStart.setDate(date.getDate() - date.getDay());
                    key = weekStart.toLocaleDateString('ko-KR');
                    break;
                }
                case 'month':
                    key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
                    break;
                case 'year':
                    key = String(date.getFullYear());
                    break;
                default:
                    key = date.toLocaleDateString('ko-KR');
            }
            if (!data[key]) {
                data[key] = { period: key, revenue: 0, count: 0, teeth: 0 };
            }
            data[key].revenue += statement.totalAmount || 0;
            data[key].count += 1;
            data[key].teeth += statement.totalTeeth || 0;
        });
        return Object.values(data).sort((a, b) => a.period.localeCompare(b.period));
    }, [filteredStatements, selectedPeriod]);

    // 거래처별 매출+건수+치아
    const tradePartnerData = useMemo(() => {
        const stats = {};
        filteredStatements.forEach(statement => {
            const partnerId = statement.type === 'sent' ? statement.toUserId : statement.fromUserId;
            const partnerName = statement.type === 'sent' ? statement.toUserName : statement.fromUserName;
            if (!partnerId) return;
            if (!stats[partnerId]) {
                stats[partnerId] = { id: partnerId, name: partnerName || '미지정', revenue: 0, count: 0, teeth: 0 };
            }
            stats[partnerId].revenue += statement.totalAmount || 0;
            stats[partnerId].count += 1;
            stats[partnerId].teeth += statement.totalTeeth || 0;
        });
        return Object.values(stats).sort((a, b) => b.revenue - a.revenue);
    }, [filteredStatements]);

    // 요일별 접수 패턴 (createdAt 기준)
    const tradeDayOfWeekData = useMemo(() => {
        const dayNames = ['일', '월', '화', '수', '목', '금', '토'];
        const dayCounts = [0, 0, 0, 0, 0, 0, 0];

        filteredStatements.forEach(statement => {
            const date = statement.createdAt?.toDate ? statement.createdAt.toDate() : new Date(statement.createdAt || Date.now());
            if (date) {
                dayCounts[date.getDay()]++;
            }
        });

        const days = Math.max(1, Math.ceil((dateRange.endDate - dateRange.startDate) / (1000 * 60 * 60 * 24)));
        const weeks = Math.max(1, days / 7);

        return dayNames.map((name, index) => ({
            day: name,
            count: dayCounts[index],
            avg: parseFloat((dayCounts[index] / weeks).toFixed(1))
        }));
    }, [filteredStatements, dateRange]);

    // ================================================
    // 탭 2: 납기 관리 (변경 없음)
    // ================================================

    // 받은 의뢰서만 (납기 관리 대상)
    const receivedOrders = useMemo(() => {
        return filteredOrders.filter(o => o.direction === 'received');
    }, [filteredOrders]);

    const deliveryStats = useMemo(() => {
        const now = new Date();
        let completedCount = 0;
        let onTimeCount = 0;
        let totalDays = 0;
        let completedWithDays = 0;
        let delayedCount = 0;
        let inProgressCount = 0;

        receivedOrders.forEach(order => {
            const status = order.status || 'pending';
            const createdAt = toDate(order.createdAt);
            const dueDate = toDate(order.dueDate);
            const completedAt = toDate(order.completedAt) || toDate(order.printedAt);

            if (status === 'completed') {
                completedCount++;
                if (dueDate && completedAt) {
                    if (completedAt <= dueDate) {
                        onTimeCount++;
                    }
                }
                if (createdAt && completedAt) {
                    const diffDays = Math.ceil((completedAt - createdAt) / (1000 * 60 * 60 * 24));
                    if (diffDays >= 0) {
                        totalDays += diffDays;
                        completedWithDays++;
                    }
                }
            } else {
                if (status === 'pending' || status === 'in_progress') {
                    inProgressCount++;
                }
                if (dueDate && dueDate < now && status !== 'completed') {
                    delayedCount++;
                }
            }
        });

        const onTimeRate = completedCount > 0
            ? parseFloat((onTimeCount / completedCount * 100).toFixed(1))
            : 0;
        const avgDays = completedWithDays > 0
            ? parseFloat((totalDays / completedWithDays).toFixed(1))
            : 0;

        return {
            onTimeRate,
            avgDays,
            delayedCount,
            inProgressCount,
            completedCount,
            totalOrders: receivedOrders.length
        };
    }, [receivedOrders]);

    // 월별 납기 준수율 추이
    const monthlyDeliveryData = useMemo(() => {
        const monthly = {};
        receivedOrders.forEach(order => {
            const date = toDate(order.createdAt);
            if (!date) return;
            const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;

            if (!monthly[monthKey]) {
                monthly[monthKey] = { month: monthKey, completed: 0, onTime: 0, total: 0 };
            }
            monthly[monthKey].total++;

            if (order.status === 'completed') {
                monthly[monthKey].completed++;
                const dueDate = toDate(order.dueDate);
                const completedAt = toDate(order.completedAt) || toDate(order.printedAt);
                if (dueDate && completedAt && completedAt <= dueDate) {
                    monthly[monthKey].onTime++;
                }
            }
        });

        return Object.values(monthly)
            .map(m => ({
                ...m,
                onTimeRate: m.completed > 0 ? parseFloat((m.onTime / m.completed * 100).toFixed(1)) : 0
            }))
            .sort((a, b) => a.month.localeCompare(b.month));
    }, [receivedOrders]);

    // 거래처별 평균 소요일
    const partnerDeliveryData = useMemo(() => {
        const partnerData = {};
        receivedOrders.forEach(order => {
            const partnerId = order.fromUserId;
            const partnerName = order.fromUserName || order.fromBusinessName || '미지정';
            if (!partnerId) return;

            if (!partnerData[partnerId]) {
                partnerData[partnerId] = { id: partnerId, name: partnerName, totalDays: 0, count: 0, completed: 0, onTime: 0 };
            }

            if (order.status === 'completed') {
                partnerData[partnerId].completed++;
                const createdAt = toDate(order.createdAt);
                const completedAt = toDate(order.completedAt) || toDate(order.printedAt);
                const dueDate = toDate(order.dueDate);

                if (createdAt && completedAt) {
                    const diffDays = Math.ceil((completedAt - createdAt) / (1000 * 60 * 60 * 24));
                    if (diffDays >= 0) {
                        partnerData[partnerId].totalDays += diffDays;
                        partnerData[partnerId].count++;
                    }
                }
                if (dueDate && completedAt && completedAt <= dueDate) {
                    partnerData[partnerId].onTime++;
                }
            }
        });

        return Object.values(partnerData)
            .map(p => ({
                ...p,
                avgDays: p.count > 0 ? parseFloat((p.totalDays / p.count).toFixed(1)) : 0,
                onTimeRate: p.completed > 0 ? parseFloat((p.onTime / p.completed * 100).toFixed(1)) : 0
            }))
            .filter(p => p.count > 0)
            .sort((a, b) => b.avgDays - a.avgDays)
            .slice(0, 10);
    }, [receivedOrders]);

    // 현재 주문 상태 분포
    const statusDistribution = useMemo(() => {
        const statusLabels = { pending: '대기중', in_progress: '진행중', completed: '완료' };
        const statusColors = { pending: '#f97316', in_progress: '#6366f1', completed: '#10b981' };
        const counts = { pending: 0, in_progress: 0, completed: 0 };

        receivedOrders.forEach(order => {
            const status = order.status || 'pending';
            if (counts[status] !== undefined) {
                counts[status]++;
            }
        });

        return Object.entries(counts)
            .filter(([, count]) => count > 0)
            .map(([status, count]) => ({
                name: statusLabels[status],
                value: count,
                color: statusColors[status]
            }));
    }, [receivedOrders]);

    // 납기 관리 - 거래처별 상세 테이블 데이터
    const deliveryPartnerTable = useMemo(() => {
        const partnerData = {};
        receivedOrders.forEach(order => {
            const partnerId = order.fromUserId;
            const partnerName = order.fromUserName || order.fromBusinessName || '미지정';
            if (!partnerId) return;

            if (!partnerData[partnerId]) {
                partnerData[partnerId] = {
                    id: partnerId, name: partnerName,
                    total: 0, completed: 0, onTime: 0,
                    totalDays: 0, daysCount: 0
                };
            }
            partnerData[partnerId].total++;

            if (order.status === 'completed') {
                partnerData[partnerId].completed++;
                const createdAt = toDate(order.createdAt);
                const completedAt = toDate(order.completedAt) || toDate(order.printedAt);
                const dueDate = toDate(order.dueDate);

                if (createdAt && completedAt) {
                    const diffDays = Math.ceil((completedAt - createdAt) / (1000 * 60 * 60 * 24));
                    if (diffDays >= 0) {
                        partnerData[partnerId].totalDays += diffDays;
                        partnerData[partnerId].daysCount++;
                    }
                }
                if (dueDate && completedAt && completedAt <= dueDate) {
                    partnerData[partnerId].onTime++;
                }
            }
        });

        return Object.values(partnerData)
            .map(p => ({
                ...p,
                avgDays: p.daysCount > 0 ? parseFloat((p.totalDays / p.daysCount).toFixed(1)) : 0,
                onTimeRate: p.completed > 0 ? parseFloat((p.onTime / p.completed * 100).toFixed(1)) : 0
            }))
            .sort((a, b) => b.total - a.total);
    }, [receivedOrders]);

    // ================================================
    // 탭 3: 리메이크 분석 (transactionStatements 전용)
    // ================================================

    const remakeOverall = useMemo(() => {
        let totalItems = 0;
        let remakeItems = 0;
        const reasonCounts = {};
        const partnerStats = {};

        // transactionStatements에서만 추출
        filteredStatements.forEach(statement => {
            if (!statement.items || !Array.isArray(statement.items)) return;
            const partnerId = statement.type === 'sent' ? statement.toUserId : statement.fromUserId;
            const partnerName = statement.type === 'sent' ? statement.toUserName : statement.fromUserName;

            statement.items.forEach(item => {
                totalItems++;
                if (partnerId) {
                    if (!partnerStats[partnerId]) {
                        partnerStats[partnerId] = { id: partnerId, name: partnerName || '미지정', total: 0, remakes: 0, reasons: {} };
                    }
                    partnerStats[partnerId].total++;
                }

                if (item.isRemake === true && item.remakeType !== 'modify') {
                    remakeItems++;
                    const reason = item.remakeReason || '사유 미기재';
                    reasonCounts[reason] = (reasonCounts[reason] || 0) + 1;
                    if (partnerId && partnerStats[partnerId]) {
                        partnerStats[partnerId].remakes++;
                        partnerStats[partnerId].reasons[reason] = (partnerStats[partnerId].reasons[reason] || 0) + 1;
                    }
                }
            });
        });

        const remakeRate = totalItems > 0 ? parseFloat((remakeItems / totalItems * 100).toFixed(1)) : 0;

        const reasonList = Object.entries(reasonCounts)
            .map(([reason, count]) => ({
                reason,
                count,
                percentage: remakeItems > 0 ? parseFloat((count / remakeItems * 100).toFixed(1)) : 0
            }))
            .sort((a, b) => b.count - a.count)
            .slice(0, 10);

        const partnerList = Object.values(partnerStats)
            .map(p => ({
                ...p,
                remakeRate: p.total > 0 ? parseFloat((p.remakes / p.total * 100).toFixed(1)) : 0,
                topReason: Object.entries(p.reasons).sort((a, b) => b[1] - a[1])[0]?.[0] || '-'
            }))
            .sort((a, b) => b.remakeRate - a.remakeRate);

        const topRemakePartner = partnerList.find(p => p.total >= 5);

        return {
            totalItems,
            remakeItems,
            remakeRate,
            topReason: reasonList[0]?.reason || '-',
            topRemakePartner: topRemakePartner?.name || '-',
            topRemakePartnerRate: topRemakePartner?.remakeRate || 0,
            reasonList,
            partnerList,
            partnerRanking: partnerList.filter(p => p.total >= 5).slice(0, 10)
        };
    }, [filteredStatements]);

    // 월별 리메이크율 추이 (transactionStatements 전용)
    const monthlyRemakeData = useMemo(() => {
        const monthly = {};

        filteredStatements.forEach(s => {
            const date = s.createdAt?.toDate ? s.createdAt.toDate() : new Date(s.createdAt || Date.now());
            const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
            if (!monthly[monthKey]) {
                monthly[monthKey] = { month: monthKey, total: 0, remakes: 0 };
            }
            if (s.items && Array.isArray(s.items)) {
                s.items.forEach(item => {
                    monthly[monthKey].total++;
                    if (item.isRemake === true && item.remakeType !== 'modify') {
                        monthly[monthKey].remakes++;
                    }
                });
            }
        });

        return Object.values(monthly)
            .map(m => ({
                ...m,
                remakeRate: m.total > 0 ? parseFloat((m.remakes / m.total * 100).toFixed(1)) : 0
            }))
            .sort((a, b) => a.month.localeCompare(b.month));
    }, [filteredStatements]);

    // ================================================
    // 탭 4: 단가 분석 (신규, transactionStatements 기반)
    // ================================================

    const priceStats = useMemo(() => {
        const totalRevenue = filteredStatements.reduce((sum, s) => sum + (s.totalAmount || 0), 0);
        const totalTeeth = filteredStatements.reduce((sum, s) => sum + (s.totalTeeth || 0), 0);
        const avgUnitPrice = totalTeeth > 0 ? Math.round(totalRevenue / totalTeeth) : 0;

        // 거래처별 평균 단가
        const partnerPrices = {};
        filteredStatements.forEach(s => {
            const pid = s.type === 'sent' ? s.toUserId : s.fromUserId;
            const pname = s.type === 'sent' ? s.toUserName : s.fromUserName;
            if (!pid) return;
            if (!partnerPrices[pid]) {
                partnerPrices[pid] = { name: pname || '미지정', revenue: 0, teeth: 0 };
            }
            partnerPrices[pid].revenue += s.totalAmount || 0;
            partnerPrices[pid].teeth += s.totalTeeth || 0;
        });

        let highestPartner = { name: '-', avgPrice: 0 };
        let lowestPartner = { name: '-', avgPrice: Infinity };
        Object.values(partnerPrices).forEach(p => {
            if (p.teeth > 0) {
                const avg = p.revenue / p.teeth;
                if (avg > highestPartner.avgPrice) {
                    highestPartner = { name: p.name, avgPrice: Math.round(avg) };
                }
                if (avg < lowestPartner.avgPrice) {
                    lowestPartner = { name: p.name, avgPrice: Math.round(avg) };
                }
            }
        });
        if (lowestPartner.avgPrice === Infinity) {
            lowestPartner = { name: '-', avgPrice: 0 };
        }

        // 할인율
        const totalSubtotal = filteredStatements.reduce((sum, s) => sum + (s.subtotalAmount || s.totalAmount || 0), 0);
        const totalDiscount = filteredStatements.reduce((sum, s) => sum + (s.discountAmount || 0), 0);
        const avgDiscountRate = totalSubtotal > 0 ? parseFloat((totalDiscount / totalSubtotal * 100).toFixed(1)) : 0;

        return { avgUnitPrice, highestPartner, lowestPartner, avgDiscountRate };
    }, [filteredStatements]);

    // 보철물별 평균 단가
    const prosthesisData = useMemo(() => {
        const prosthesisMap = {};
        const keywords = {
            '크라운': ['크라운', 'crown', 'cr'],
            '브릿지': ['브릿지', 'bridge', 'br'],
            '인레이': ['인레이', 'inlay'],
            '온레이': ['온레이', 'onlay'],
            '임플란트': ['임플란트', 'implant', 'imp'],
            '라미네이트': ['라미네이트', 'laminate', 'veneer'],
            '틀니': ['틀니', 'denture'],
            '교정': ['교정', 'ortho']
        };

        filteredStatements.forEach(s => {
            if (!s.items || !Array.isArray(s.items)) return;
            s.items.forEach(item => {
                const info = (item.toothInfo || item.prosthesisType || item.name || '').toLowerCase();
                let matched = false;
                for (const [name, kws] of Object.entries(keywords)) {
                    if (kws.some(kw => info.includes(kw.toLowerCase()))) {
                        if (!prosthesisMap[name]) {
                            prosthesisMap[name] = { name, totalRevenue: 0, count: 0 };
                        }
                        prosthesisMap[name].totalRevenue += item.amount || item.unitPrice || 0;
                        prosthesisMap[name].count += item.teeth || 1;
                        matched = true;
                        break;
                    }
                }
                if (!matched) {
                    const etcName = '기타';
                    if (!prosthesisMap[etcName]) {
                        prosthesisMap[etcName] = { name: etcName, totalRevenue: 0, count: 0 };
                    }
                    prosthesisMap[etcName].totalRevenue += item.amount || item.unitPrice || 0;
                    prosthesisMap[etcName].count += item.teeth || 1;
                }
            });
        });

        return Object.values(prosthesisMap)
            .map(p => ({
                ...p,
                avgPrice: p.count > 0 ? Math.round(p.totalRevenue / p.count) : 0
            }))
            .filter(p => p.count > 0)
            .sort((a, b) => b.avgPrice - a.avgPrice);
    }, [filteredStatements]);

    // 거래처별 평균 단가
    const partnerPriceData = useMemo(() => {
        const stats = {};
        filteredStatements.forEach(s => {
            const pid = s.type === 'sent' ? s.toUserId : s.fromUserId;
            const pname = s.type === 'sent' ? s.toUserName : s.fromUserName;
            if (!pid) return;
            if (!stats[pid]) {
                stats[pid] = { id: pid, name: pname || '미지정', revenue: 0, teeth: 0 };
            }
            stats[pid].revenue += s.totalAmount || 0;
            stats[pid].teeth += s.totalTeeth || 0;
        });
        return Object.values(stats)
            .map(p => ({
                ...p,
                avgPrice: p.teeth > 0 ? Math.round(p.revenue / p.teeth) : 0
            }))
            .filter(p => p.teeth > 0)
            .sort((a, b) => b.avgPrice - a.avgPrice);
    }, [filteredStatements]);

    // 월별 평균 단가 추이
    const monthlyPriceData = useMemo(() => {
        const monthly = {};
        filteredStatements.forEach(s => {
            const date = s.createdAt?.toDate ? s.createdAt.toDate() : new Date(s.createdAt || Date.now());
            const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
            if (!monthly[monthKey]) {
                monthly[monthKey] = { month: monthKey, revenue: 0, teeth: 0 };
            }
            monthly[monthKey].revenue += s.totalAmount || 0;
            monthly[monthKey].teeth += s.totalTeeth || 0;
        });
        return Object.values(monthly)
            .map(m => ({
                ...m,
                avgPrice: m.teeth > 0 ? Math.round(m.revenue / m.teeth) : 0
            }))
            .sort((a, b) => a.month.localeCompare(b.month));
    }, [filteredStatements]);

    // ================================================
    // 유틸리티
    // ================================================

    const exportToExcel = () => {
        try {
            let csv = '날짜,발행/수신,상대방,항목수,치아개수,금액,메모\n';
            filteredStatements.forEach(statement => {
                const date = statement.createdAt?.toDate().toLocaleDateString('ko-KR') || '-';
                const type = statement.type === 'sent' ? '발행' : '수신';
                const partnerName = statement.type === 'sent' ?
                    (statement.toUserName || '-') :
                    (statement.fromUserName || '-');
                const itemCount = statement.items?.length || 0;
                const teethCount = statement.totalTeeth || 0;
                const amount = statement.totalAmount || 0;
                const notes = (statement.notes || '').replace(/,/g, ';');
                csv += `${date},${type},${partnerName},${itemCount},${teethCount},${amount},"${notes}"\n`;
            });

            csv += '\n통계 요약\n';
            csv += `총 거래명세서,${tradeStats.totalStatements}건\n`;
            csv += `총 매출,${tradeStats.totalRevenue}원\n`;
            csv += `총 치아 개수,${tradeStats.totalTeeth}개\n`;
            csv += `평균 금액,${Math.round(tradeStats.avgAmount)}원\n`;

            const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
            const link = document.createElement('a');
            const url = URL.createObjectURL(blob);
            link.setAttribute('href', url);
            link.setAttribute('download', `거래통계_${new Date().toISOString().split('T')[0]}.csv`);
            link.style.visibility = 'hidden';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } catch (error) {
            console.error('엑셀 다운로드 실패:', error);
            alert('엑셀 다운로드에 실패했습니다.');
        }
    };

    const formatNumber = (num) => {
        return new Intl.NumberFormat('ko-KR').format(Math.round(num));
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('ko-KR', {
            style: 'currency',
            currency: 'KRW'
        }).format(Math.round(amount));
    };

    const CustomTooltip = ({ active, payload, label }) => {
        if (active && payload && payload.length) {
            return (
                <div className="custom-tooltip">
                    <p className="label">{label}</p>
                    {payload.map((entry, index) => (
                        <p key={index} style={{ color: entry.color }}>
                            {entry.name}: {entry.name.includes('매출') || entry.name.includes('금액') || entry.name.includes('단가') ?
                                formatCurrency(entry.value) :
                                entry.name.includes('율') ? entry.value + '%' :
                                formatNumber(entry.value) + (entry.name.includes('치아') ? '개' : entry.name.includes('일') ? '일' : '건')}
                        </p>
                    ))}
                </div>
            );
        }
        return null;
    };

    const COLORS = ['#6366f1', '#8b5cf6', '#ec4899', '#f43f5e', '#f97316', '#fbbf24', '#84cc16', '#10b981'];

    const resetFilters = () => {
        setDateRange({
            startDate: new Date(new Date().setMonth(new Date().getMonth() - 3)),
            endDate: new Date()
        });
        setSelectedPartner('all');
    };

    // ================================================
    // 렌더링
    // ================================================

    return (
        <div className="statistics-container">
            {/* 헤더 */}
            <div className="statistics-header">
                <div className="header-content">
                    <h1>통계 분석</h1>
                    <p>거래, 납기, 리메이크 및 단가 분석</p>
                </div>
                <div className="header-actions">
                    <button onClick={loadAllData} className="btn-refresh" disabled={loading}>
                        <RefreshCw size={18} />
                        새로고침
                    </button>
                    <button
                        onClick={exportToExcel}
                        className="btn-export"
                        disabled={loading || filteredStatements.length === 0}
                    >
                        <Download size={18} />
                        엑셀 다운로드
                    </button>
                </div>
            </div>

            {/* 탭 메뉴 */}
            <div className="statistics-tabs">
                <button
                    className={`tab-button ${activeTab === 'trade' ? 'active' : ''}`}
                    onClick={() => setActiveTab('trade')}
                >
                    <FileText size={18} />
                    거래 통계
                </button>
                {/* 납기 관리 탭 제거 */}
                <button
                    className={`tab-button ${activeTab === 'remake' ? 'active' : ''}`}
                    onClick={() => setActiveTab('remake')}
                >
                    <RefreshCw size={18} />
                    리메이크 분석
                </button>
                <button
                    className={`tab-button ${activeTab === 'price' ? 'active' : ''}`}
                    onClick={() => setActiveTab('price')}
                >
                    <DollarSign size={18} />
                    단가 분석
                </button>
            </div>

            {/* 필터 섹션 */}
            <div className="filter-section">
                <div className="filter-group">
                    <label>기간 선택</label>
                    <div className="date-range-picker">
                        <DatePicker
                            selected={dateRange.startDate}
                            onChange={date => setDateRange(prev => ({ ...prev, startDate: date }))}
                            selectsStart
                            startDate={dateRange.startDate}
                            endDate={dateRange.endDate}
                            locale={ko}
                            dateFormat="yyyy-MM-dd"
                            className="date-input"
                        />
                        <span>~</span>
                        <DatePicker
                            selected={dateRange.endDate}
                            onChange={date => setDateRange(prev => ({ ...prev, endDate: date }))}
                            selectsEnd
                            startDate={dateRange.startDate}
                            endDate={dateRange.endDate}
                            minDate={dateRange.startDate}
                            locale={ko}
                            dateFormat="yyyy-MM-dd"
                            className="date-input"
                        />
                    </div>
                </div>

                <div className="filter-group">
                    <label>집계 단위</label>
                    <select
                        value={selectedPeriod}
                        onChange={(e) => setSelectedPeriod(e.target.value)}
                        className="filter-select"
                    >
                        <option value="day">일별</option>
                        <option value="week">주별</option>
                        <option value="month">월별</option>
                        <option value="year">년별</option>
                    </select>
                </div>

                <div className="filter-group">
                    <label>거래처 필터</label>
                    <select
                        value={selectedPartner}
                        onChange={(e) => setSelectedPartner(e.target.value)}
                        className="filter-select"
                    >
                        <option value="all">전체 거래처 ({partners.length})</option>
                        {partners.map(partner => (
                            <option key={partner.id} value={partner.id}>
                                {partner.name}
                            </option>
                        ))}
                    </select>
                </div>
            </div>

            {loading ? (
                <div className="loading-container">
                    <div className="spinner"></div>
                    <p>데이터를 불러오는 중...</p>
                </div>
            ) : (
                <>
                    {/* ========================== */}
                    {/* 탭 1: 거래 통계            */}
                    {/* ========================== */}
                    {activeTab === 'trade' && (
                        <>
                            {filteredStatements.length === 0 ? (
                                <div className="empty-state">
                                    <FileText size={64} color="#cbd5e1" />
                                    <h3>거래명세서가 없습니다</h3>
                                    <p>선택한 기간에 거래명세서가 없습니다.</p>
                                    <button onClick={resetFilters} className="btn-reset">
                                        필터 초기화
                                    </button>
                                </div>
                            ) : (
                                <>
                                    {/* 핵심 지표 카드 */}
                                    <div className="stats-cards">
                                        <div className="stat-card revenue">
                                            <div className="stat-icon">
                                                <FileText size={24} />
                                            </div>
                                            <div className="stat-content">
                                                <div className="stat-label">총 거래명세서 건수</div>
                                                <div className="stat-value">{formatNumber(tradeStats.totalStatements)}건</div>
                                                <div className="stat-sub">
                                                    {tradeStats.countGrowth !== 0 && (
                                                        <span className={tradeStats.countGrowth > 0 ? 'growth-up' : 'growth-down'}>
                                                            {tradeStats.countGrowth > 0 ? <TrendingUp size={14} /> : <TrendingDown size={14} />}
                                                            {Math.abs(tradeStats.countGrowth)}% (전 기간 대비)
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        </div>

                                        <div className="stat-card teeth">
                                            <div className="stat-icon">
                                                <Activity size={24} />
                                            </div>
                                            <div className="stat-content">
                                                <div className="stat-label">총 치아 수</div>
                                                <div className="stat-value">{formatNumber(tradeStats.totalTeeth)}개</div>
                                                <div className="stat-sub">
                                                    일평균 {tradeStats.dailyAvg}건
                                                </div>
                                            </div>
                                        </div>

                                        <div className="stat-card statements">
                                            <div className="stat-icon">
                                                <DollarSign size={24} />
                                            </div>
                                            <div className="stat-content">
                                                <div className="stat-label">총 매출액</div>
                                                <div className="stat-value">{formatCurrency(tradeStats.totalRevenue)}</div>
                                                <div className="stat-sub">
                                                    {tradeStats.revenueGrowth !== 0 && (
                                                        <span className={tradeStats.revenueGrowth > 0 ? 'growth-up' : 'growth-down'}>
                                                            {tradeStats.revenueGrowth > 0 ? <TrendingUp size={14} /> : <TrendingDown size={14} />}
                                                            {Math.abs(tradeStats.revenueGrowth)}% (전 기간 대비)
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        </div>

                                        <div className="stat-card partners">
                                            <div className="stat-icon">
                                                <Users size={24} />
                                            </div>
                                            <div className="stat-content">
                                                <div className="stat-label">활성 거래처 수</div>
                                                <div className="stat-value">{formatNumber(tradeStats.activePartners)}곳</div>
                                                <div className="stat-sub">
                                                    기간 내 거래 발생
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* 차트 */}
                                    <div className="charts-grid">
                                        {/* 매출/건수 추이 (듀얼 축) */}
                                        <div className="chart-card full-width">
                                            <div className="chart-header">
                                                <h3>매출/건수 추이</h3>
                                                <div className="chart-legend">
                                                    <span className="legend-item">
                                                        <span className="legend-dot revenue"></span>매출
                                                    </span>
                                                    <span className="legend-item">
                                                        <span className="legend-dot orders"></span>거래 건수
                                                    </span>
                                                </div>
                                            </div>
                                            <ResponsiveContainer width="100%" height={300}>
                                                <AreaChart data={tradePeriodData}>
                                                    <defs>
                                                        <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                                                            <stop offset="5%" stopColor="#6366f1" stopOpacity={0.8}/>
                                                            <stop offset="95%" stopColor="#6366f1" stopOpacity={0}/>
                                                        </linearGradient>
                                                    </defs>
                                                    <CartesianGrid strokeDasharray="3 3" />
                                                    <XAxis dataKey="period" />
                                                    <YAxis yAxisId="left" />
                                                    <YAxis yAxisId="right" orientation="right" />
                                                    <Tooltip content={<CustomTooltip />} />
                                                    <Area
                                                        yAxisId="left"
                                                        type="monotone"
                                                        dataKey="revenue"
                                                        stroke="#6366f1"
                                                        fillOpacity={1}
                                                        fill="url(#colorRevenue)"
                                                        name="매출"
                                                    />
                                                    <Line
                                                        yAxisId="right"
                                                        type="monotone"
                                                        dataKey="count"
                                                        stroke="#ec4899"
                                                        strokeWidth={2}
                                                        name="거래 건수"
                                                    />
                                                </AreaChart>
                                            </ResponsiveContainer>
                                        </div>

                                        {/* 거래처별 매출 TOP 10 */}
                                        {tradePartnerData.length > 0 && (
                                            <div className="chart-card">
                                                <div className="chart-header">
                                                    <h3>거래처별 매출 TOP 10</h3>
                                                </div>
                                                <ResponsiveContainer width="100%" height={300}>
                                                    <BarChart
                                                        data={tradePartnerData.slice(0, 10)}
                                                        layout="vertical"
                                                        margin={{ left: 20, right: 20 }}
                                                    >
                                                        <CartesianGrid strokeDasharray="3 3" />
                                                        <XAxis type="number" />
                                                        <YAxis dataKey="name" type="category" width={100} />
                                                        <Tooltip content={<CustomTooltip />} />
                                                        <Bar
                                                            dataKey="revenue"
                                                            fill="#6366f1"
                                                            radius={[0, 8, 8, 0]}
                                                            name="매출"
                                                        />
                                                    </BarChart>
                                                </ResponsiveContainer>
                                            </div>
                                        )}

                                        {/* 거래처별 치아 수 */}
                                        {tradePartnerData.length > 0 && (
                                            <div className="chart-card">
                                                <div className="chart-header">
                                                    <h3>거래처별 치아 수</h3>
                                                </div>
                                                <ResponsiveContainer width="100%" height={300}>
                                                    <BarChart
                                                        data={tradePartnerData.slice(0, 10)}
                                                        layout="vertical"
                                                        margin={{ left: 20, right: 20 }}
                                                    >
                                                        <CartesianGrid strokeDasharray="3 3" />
                                                        <XAxis type="number" />
                                                        <YAxis dataKey="name" type="category" width={100} />
                                                        <Tooltip content={<CustomTooltip />} />
                                                        <Bar
                                                            dataKey="teeth"
                                                            fill="#10b981"
                                                            radius={[0, 8, 8, 0]}
                                                            name="치아 수"
                                                        />
                                                    </BarChart>
                                                </ResponsiveContainer>
                                            </div>
                                        )}

                                        {/* 요일별 접수 패턴 */}
                                        <div className="chart-card">
                                            <div className="chart-header">
                                                <h3>요일별 접수 패턴</h3>
                                            </div>
                                            <ResponsiveContainer width="100%" height={300}>
                                                <BarChart data={tradeDayOfWeekData}>
                                                    <CartesianGrid strokeDasharray="3 3" />
                                                    <XAxis dataKey="day" />
                                                    <YAxis />
                                                    <Tooltip content={<CustomTooltip />} />
                                                    <Bar
                                                        dataKey="avg"
                                                        fill="#8b5cf6"
                                                        radius={[8, 8, 0, 0]}
                                                        name="평균 건수"
                                                    />
                                                </BarChart>
                                            </ResponsiveContainer>
                                        </div>
                                    </div>

                                    {/* 거래처별 상세 테이블 */}
                                    <div className="detailed-stats">
                                        <h3>거래처별 상세</h3>
                                        <div className="stats-table-container">
                                            <table className="stats-table">
                                                <thead>
                                                    <tr>
                                                        <th>순위</th>
                                                        <th>거래처명</th>
                                                        <th>건수</th>
                                                        <th>매출</th>
                                                        <th>평균금액</th>
                                                        <th>치아수</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {tradePartnerData.map((partner, idx) => (
                                                        <tr key={partner.id}>
                                                            <td className="rank">#{idx + 1}</td>
                                                            <td className="partner-name">{partner.name}</td>
                                                            <td>{formatNumber(partner.count)}건</td>
                                                            <td className="revenue">{formatCurrency(partner.revenue)}</td>
                                                            <td>{formatCurrency(partner.count > 0 ? partner.revenue / partner.count : 0)}</td>
                                                            <td>{formatNumber(partner.teeth)}개</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                </>
                            )}
                        </>
                    )}

                    {/* ========================== */}
                    {/* 탭 2: 납기 관리             */}
                    {/* ========================== */}
                    {activeTab === 'delivery' && (
                        <>
                            {receivedOrders.length === 0 ? (
                                <div className="empty-state">
                                    <Clock size={64} color="#cbd5e1" />
                                    <h3>받은 의뢰서가 없습니다</h3>
                                    <p>선택한 기간에 받은 의뢰서가 없습니다.</p>
                                    <button onClick={resetFilters} className="btn-reset">
                                        필터 초기화
                                    </button>
                                </div>
                            ) : (
                                <>
                                    {/* 핵심 지표 카드 */}
                                    <div className="stats-cards">
                                        <div className="stat-card partners">
                                            <div className="stat-icon">
                                                <CheckCircle size={24} />
                                            </div>
                                            <div className="stat-content">
                                                <div className="stat-label">납기 준수율</div>
                                                <div className="stat-value">{deliveryStats.onTimeRate}%</div>
                                                <div className="stat-sub">
                                                    완료 {deliveryStats.completedCount}건 중 납기 내 완료
                                                </div>
                                            </div>
                                        </div>

                                        <div className="stat-card statements">
                                            <div className="stat-icon">
                                                <Clock size={24} />
                                            </div>
                                            <div className="stat-content">
                                                <div className="stat-label">평균 제작 소요일</div>
                                                <div className="stat-value">{deliveryStats.avgDays}일</div>
                                                <div className="stat-sub">
                                                    접수 ~ 완료까지 평균
                                                </div>
                                            </div>
                                        </div>

                                        <div className="stat-card remake">
                                            <div className="stat-icon">
                                                <AlertTriangle size={24} />
                                            </div>
                                            <div className="stat-content">
                                                <div className="stat-label">지연 건수</div>
                                                <div className="stat-value">{formatNumber(deliveryStats.delayedCount)}건</div>
                                                <div className="stat-sub">
                                                    납기일 초과 미완료
                                                </div>
                                            </div>
                                        </div>

                                        <div className="stat-card teeth">
                                            <div className="stat-icon">
                                                <Activity size={24} />
                                            </div>
                                            <div className="stat-content">
                                                <div className="stat-label">현재 진행중</div>
                                                <div className="stat-value">{formatNumber(deliveryStats.inProgressCount)}건</div>
                                                <div className="stat-sub">
                                                    대기 + 진행중
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* 차트 */}
                                    <div className="charts-grid">
                                        {/* 월별 납기 준수율 추이 */}
                                        {monthlyDeliveryData.length > 0 && (
                                            <div className="chart-card full-width">
                                                <div className="chart-header">
                                                    <h3>월별 납기 준수율 추이</h3>
                                                </div>
                                                <ResponsiveContainer width="100%" height={300}>
                                                    <LineChart data={monthlyDeliveryData}>
                                                        <CartesianGrid strokeDasharray="3 3" />
                                                        <XAxis dataKey="month" />
                                                        <YAxis domain={[0, 100]} unit="%" />
                                                        <Tooltip content={<CustomTooltip />} />
                                                        <Line
                                                            type="monotone"
                                                            dataKey="onTimeRate"
                                                            stroke="#10b981"
                                                            strokeWidth={3}
                                                            dot={{ r: 5 }}
                                                            name="납기 준수율"
                                                        />
                                                    </LineChart>
                                                </ResponsiveContainer>
                                            </div>
                                        )}

                                        {/* 거래처별 평균 소요일 */}
                                        {partnerDeliveryData.length > 0 && (
                                            <div className="chart-card">
                                                <div className="chart-header">
                                                    <h3>거래처별 평균 소요일</h3>
                                                </div>
                                                <ResponsiveContainer width="100%" height={300}>
                                                    <BarChart
                                                        data={partnerDeliveryData}
                                                        layout="vertical"
                                                        margin={{ left: 20, right: 20 }}
                                                    >
                                                        <CartesianGrid strokeDasharray="3 3" />
                                                        <XAxis type="number" unit="일" />
                                                        <YAxis dataKey="name" type="category" width={100} />
                                                        <Tooltip content={<CustomTooltip />} />
                                                        <Bar
                                                            dataKey="avgDays"
                                                            fill="#6366f1"
                                                            radius={[0, 8, 8, 0]}
                                                            name="평균 소요일"
                                                        />
                                                    </BarChart>
                                                </ResponsiveContainer>
                                            </div>
                                        )}

                                        {/* 현재 주문 상태 분포 */}
                                        {statusDistribution.length > 0 && (
                                            <div className="chart-card">
                                                <div className="chart-header">
                                                    <h3>주문 상태 분포</h3>
                                                </div>
                                                <ResponsiveContainer width="100%" height={250}>
                                                    <PieChart>
                                                        <Pie
                                                            data={statusDistribution}
                                                            cx="50%"
                                                            cy="50%"
                                                            labelLine={false}
                                                            label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                                                            outerRadius={80}
                                                            fill="#8884d8"
                                                            dataKey="value"
                                                        >
                                                            {statusDistribution.map((entry, index) => (
                                                                <Cell key={`cell-${index}`} fill={entry.color} />
                                                            ))}
                                                        </Pie>
                                                        <Tooltip />
                                                    </PieChart>
                                                </ResponsiveContainer>
                                                <div className="type-stats-details">
                                                    {statusDistribution.map((stat, idx) => (
                                                        <div key={idx} className="type-stat-item">
                                                            <span style={{ color: stat.color }}>● {stat.name}</span>
                                                            <span>{stat.value}건</span>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    {/* 거래처별 상세 테이블 */}
                                    <div className="detailed-stats">
                                        <h3>거래처별 납기 상세</h3>
                                        <div className="stats-table-container">
                                            <table className="stats-table">
                                                <thead>
                                                    <tr>
                                                        <th>순위</th>
                                                        <th>거래처명</th>
                                                        <th>의뢰 건수</th>
                                                        <th>완료 건수</th>
                                                        <th>납기 준수율</th>
                                                        <th>평균 소요일</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {deliveryPartnerTable.map((partner, idx) => (
                                                        <tr key={partner.id}>
                                                            <td className="rank">#{idx + 1}</td>
                                                            <td className="partner-name">{partner.name}</td>
                                                            <td>{formatNumber(partner.total)}건</td>
                                                            <td>{formatNumber(partner.completed)}건</td>
                                                            <td className={partner.onTimeRate >= 90 ? 'remake-low' : partner.onTimeRate >= 70 ? 'remake-medium' : 'remake-high'}>
                                                                {partner.onTimeRate}%
                                                            </td>
                                                            <td>{partner.avgDays}일</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                </>
                            )}
                        </>
                    )}

                    {/* ========================== */}
                    {/* 탭 3: 리메이크 분석         */}
                    {/* ========================== */}
                    {activeTab === 'remake' && (
                        <>
                            {remakeOverall.totalItems === 0 ? (
                                <div className="empty-state">
                                    <RefreshCw size={64} color="#cbd5e1" />
                                    <h3>데이터가 없습니다</h3>
                                    <p>선택한 기간에 분석할 데이터가 없습니다.</p>
                                    <button onClick={resetFilters} className="btn-reset">
                                        필터 초기화
                                    </button>
                                </div>
                            ) : (
                                <>
                                    {/* 핵심 지표 카드 */}
                                    <div className="stats-cards">
                                        <div className="stat-card remake">
                                            <div className="stat-icon">
                                                <RefreshCw size={24} />
                                            </div>
                                            <div className="stat-content">
                                                <div className="stat-label">전체 리메이크율</div>
                                                <div className="stat-value">{remakeOverall.remakeRate}%</div>
                                                <div className="stat-sub">
                                                    전체 항목 대비
                                                </div>
                                            </div>
                                        </div>

                                        <div className="stat-card statements">
                                            <div className="stat-icon">
                                                <Package size={24} />
                                            </div>
                                            <div className="stat-content">
                                                <div className="stat-label">리메이크 건수</div>
                                                <div className="stat-value">{formatNumber(remakeOverall.remakeItems)}건</div>
                                                <div className="stat-sub">
                                                    총 {formatNumber(remakeOverall.totalItems)}건 중
                                                </div>
                                            </div>
                                        </div>

                                        <div className="stat-card teeth">
                                            <div className="stat-icon">
                                                <AlertTriangle size={24} />
                                            </div>
                                            <div className="stat-content">
                                                <div className="stat-label">최다 리메이크 사유</div>
                                                <div className="stat-value" style={{ fontSize: '18px' }}>{remakeOverall.topReason}</div>
                                                <div className="stat-sub">
                                                    {remakeOverall.reasonList[0]?.count || 0}건 발생
                                                </div>
                                            </div>
                                        </div>

                                        <div className="stat-card revenue">
                                            <div className="stat-icon">
                                                <Award size={24} />
                                            </div>
                                            <div className="stat-content">
                                                <div className="stat-label">리메이크율 최고 거래처</div>
                                                <div className="stat-value" style={{ fontSize: '18px' }}>{remakeOverall.topRemakePartner}</div>
                                                <div className="stat-sub">
                                                    {remakeOverall.topRemakePartnerRate}%
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* 차트 */}
                                    <div className="charts-grid">
                                        {/* 월별 리메이크율 추이 */}
                                        {monthlyRemakeData.length > 0 && (
                                            <div className="chart-card full-width">
                                                <div className="chart-header">
                                                    <h3>월별 리메이크율 추이</h3>
                                                </div>
                                                <ResponsiveContainer width="100%" height={300}>
                                                    <LineChart data={monthlyRemakeData}>
                                                        <CartesianGrid strokeDasharray="3 3" />
                                                        <XAxis dataKey="month" />
                                                        <YAxis unit="%" />
                                                        <Tooltip content={<CustomTooltip />} />
                                                        <Line
                                                            type="monotone"
                                                            dataKey="remakeRate"
                                                            stroke="#dc2626"
                                                            strokeWidth={3}
                                                            dot={{ r: 5 }}
                                                            name="리메이크율"
                                                        />
                                                    </LineChart>
                                                </ResponsiveContainer>
                                            </div>
                                        )}

                                        {/* 리메이크 사유 분석 */}
                                        {remakeOverall.reasonList.length > 0 && (
                                            <div className="chart-card">
                                                <div className="chart-header">
                                                    <h3>리메이크 사유 분석</h3>
                                                    <span className="chart-badge">총 {remakeOverall.remakeItems}건</span>
                                                </div>
                                                <ResponsiveContainer width="100%" height={300}>
                                                    <PieChart>
                                                        <Pie
                                                            data={remakeOverall.reasonList}
                                                            cx="50%"
                                                            cy="50%"
                                                            labelLine={false}
                                                            label={({ reason, percentage }) =>
                                                                `${reason.length > 10 ? reason.substring(0, 10) + '...' : reason} (${percentage}%)`
                                                            }
                                                            outerRadius={90}
                                                            fill="#8884d8"
                                                            dataKey="count"
                                                        >
                                                            {remakeOverall.reasonList.map((entry, index) => (
                                                                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                                            ))}
                                                        </Pie>
                                                        <Tooltip
                                                            content={({ active, payload }) => {
                                                                if (active && payload && payload.length) {
                                                                    const data = payload[0].payload;
                                                                    return (
                                                                        <div className="custom-tooltip">
                                                                            <p className="label">{data.reason}</p>
                                                                            <p style={{ color: payload[0].fill }}>
                                                                                발생 건수: {data.count}건
                                                                            </p>
                                                                            <p style={{ color: '#64748b' }}>
                                                                                비율: {data.percentage}%
                                                                            </p>
                                                                        </div>
                                                                    );
                                                                }
                                                                return null;
                                                            }}
                                                        />
                                                    </PieChart>
                                                </ResponsiveContainer>
                                                <div className="remake-reasons-list">
                                                    {remakeOverall.reasonList.slice(0, 5).map((item, idx) => (
                                                        <div key={idx} className="reason-item">
                                                            <span className="reason-rank">#{idx + 1}</span>
                                                            <span className="reason-name">{item.reason}</span>
                                                            <span className="reason-count">{item.count}건</span>
                                                            <span className="reason-percentage">{item.percentage}%</span>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}

                                        {/* 거래처별 리메이크율 TOP 10 */}
                                        {remakeOverall.partnerRanking.length > 0 && (
                                            <div className="chart-card">
                                                <div className="chart-header">
                                                    <h3>거래처별 리메이크율 TOP 10</h3>
                                                    <span className="chart-badge warning">주의 필요</span>
                                                </div>
                                                <ResponsiveContainer width="100%" height={350}>
                                                    <BarChart
                                                        data={remakeOverall.partnerRanking}
                                                        layout="vertical"
                                                        margin={{ left: 20, right: 40 }}
                                                    >
                                                        <CartesianGrid strokeDasharray="3 3" />
                                                        <XAxis type="number" unit="%" />
                                                        <YAxis dataKey="name" type="category" width={100} />
                                                        <Tooltip
                                                            content={({ active, payload }) => {
                                                                if (active && payload && payload.length) {
                                                                    const data = payload[0].payload;
                                                                    return (
                                                                        <div className="custom-tooltip">
                                                                            <p className="label">{data.name}</p>
                                                                            <p style={{ color: '#dc2626' }}>
                                                                                리메이크율: {data.remakeRate}%
                                                                            </p>
                                                                            <p style={{ color: '#64748b' }}>
                                                                                리메이크: {data.remakes}건 / 총 {data.total}건
                                                                            </p>
                                                                        </div>
                                                                    );
                                                                }
                                                                return null;
                                                            }}
                                                        />
                                                        <Bar
                                                            dataKey="remakeRate"
                                                            fill="#dc2626"
                                                            radius={[0, 8, 8, 0]}
                                                            name="리메이크율"
                                                        >
                                                            {remakeOverall.partnerRanking.map((entry, index) => (
                                                                <Cell
                                                                    key={`cell-${index}`}
                                                                    fill={entry.remakeRate > 15 ? '#dc2626' : entry.remakeRate > 10 ? '#f97316' : '#fbbf24'}
                                                                />
                                                            ))}
                                                        </Bar>
                                                    </BarChart>
                                                </ResponsiveContainer>
                                                <div className="remake-rate-legend">
                                                    <span className="legend-item high">
                                                        <span className="legend-dot" style={{backgroundColor: '#dc2626'}}></span>
                                                        15% 이상 (높음)
                                                    </span>
                                                    <span className="legend-item medium">
                                                        <span className="legend-dot" style={{backgroundColor: '#f97316'}}></span>
                                                        10-15% (보통)
                                                    </span>
                                                    <span className="legend-item low">
                                                        <span className="legend-dot" style={{backgroundColor: '#fbbf24'}}></span>
                                                        10% 미만 (양호)
                                                    </span>
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    {/* 거래처별 상세 테이블 */}
                                    <div className="detailed-stats">
                                        <h3>거래처별 리메이크 상세</h3>
                                        <div className="stats-table-container">
                                            <table className="stats-table">
                                                <thead>
                                                    <tr>
                                                        <th>순위</th>
                                                        <th>거래처명</th>
                                                        <th>총 건수</th>
                                                        <th>리메이크 건수</th>
                                                        <th>리메이크율</th>
                                                        <th>주요 사유</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {remakeOverall.partnerList
                                                        .filter(p => p.total >= 1)
                                                        .slice(0, 20)
                                                        .map((partner, idx) => (
                                                            <tr key={partner.id}>
                                                                <td className="rank">#{idx + 1}</td>
                                                                <td className="partner-name">{partner.name}</td>
                                                                <td>{formatNumber(partner.total)}건</td>
                                                                <td>{formatNumber(partner.remakes)}건</td>
                                                                <td className={partner.remakeRate > 15 ? 'remake-high' : partner.remakeRate > 10 ? 'remake-medium' : 'remake-low'}>
                                                                    {partner.remakeRate}%
                                                                </td>
                                                                <td>{partner.topReason}</td>
                                                            </tr>
                                                        ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                </>
                            )}
                        </>
                    )}

                    {/* ========================== */}
                    {/* 탭 4: 단가 분석             */}
                    {/* ========================== */}
                    {activeTab === 'price' && (
                        <>
                            {filteredStatements.length === 0 ? (
                                <div className="empty-state">
                                    <DollarSign size={64} color="#cbd5e1" />
                                    <h3>데이터가 없습니다</h3>
                                    <p>선택한 기간에 거래명세서가 없습니다.</p>
                                    <button onClick={resetFilters} className="btn-reset">
                                        필터 초기화
                                    </button>
                                </div>
                            ) : (
                                <>
                                    {/* 핵심 지표 카드 */}
                                    <div className="stats-cards">
                                        <div className="stat-card revenue">
                                            <div className="stat-icon">
                                                <DollarSign size={24} />
                                            </div>
                                            <div className="stat-content">
                                                <div className="stat-label">개당 평균 단가</div>
                                                <div className="stat-value">{formatCurrency(priceStats.avgUnitPrice)}</div>
                                                <div className="stat-sub">
                                                    총매출 / 총치아수
                                                </div>
                                            </div>
                                        </div>

                                        <div className="stat-card partners">
                                            <div className="stat-icon">
                                                <TrendingUp size={24} />
                                            </div>
                                            <div className="stat-content">
                                                <div className="stat-label">최고 단가 거래처</div>
                                                <div className="stat-value" style={{ fontSize: '18px' }}>{priceStats.highestPartner.name}</div>
                                                <div className="stat-sub">
                                                    {formatCurrency(priceStats.highestPartner.avgPrice)}
                                                </div>
                                            </div>
                                        </div>

                                        <div className="stat-card teeth">
                                            <div className="stat-icon">
                                                <TrendingDown size={24} />
                                            </div>
                                            <div className="stat-content">
                                                <div className="stat-label">최저 단가 거래처</div>
                                                <div className="stat-value" style={{ fontSize: '18px' }}>{priceStats.lowestPartner.name}</div>
                                                <div className="stat-sub">
                                                    {formatCurrency(priceStats.lowestPartner.avgPrice)}
                                                </div>
                                            </div>
                                        </div>

                                        <div className="stat-card statements">
                                            <div className="stat-icon">
                                                <Activity size={24} />
                                            </div>
                                            <div className="stat-content">
                                                <div className="stat-label">평균 할인율</div>
                                                <div className="stat-value">{priceStats.avgDiscountRate}%</div>
                                                <div className="stat-sub">
                                                    전체 거래 기준
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* 차트 */}
                                    <div className="charts-grid">
                                        {/* 보철물별 평균 단가 */}
                                        {prosthesisData.length > 0 && (
                                            <div className="chart-card full-width">
                                                <div className="chart-header">
                                                    <h3>보철물별 평균 단가</h3>
                                                </div>
                                                <ResponsiveContainer width="100%" height={300}>
                                                    <BarChart data={prosthesisData}>
                                                        <CartesianGrid strokeDasharray="3 3" />
                                                        <XAxis dataKey="name" />
                                                        <YAxis />
                                                        <Tooltip content={<CustomTooltip />} />
                                                        <Bar
                                                            dataKey="avgPrice"
                                                            fill="#6366f1"
                                                            radius={[8, 8, 0, 0]}
                                                            name="평균 단가"
                                                        >
                                                            {prosthesisData.map((entry, index) => (
                                                                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                                            ))}
                                                        </Bar>
                                                    </BarChart>
                                                </ResponsiveContainer>
                                            </div>
                                        )}

                                        {/* 거래처별 평균 단가 비교 */}
                                        {partnerPriceData.length > 0 && (
                                            <div className="chart-card">
                                                <div className="chart-header">
                                                    <h3>거래처별 평균 단가 비교</h3>
                                                </div>
                                                <ResponsiveContainer width="100%" height={300}>
                                                    <BarChart
                                                        data={partnerPriceData.slice(0, 10)}
                                                        layout="vertical"
                                                        margin={{ left: 20, right: 20 }}
                                                    >
                                                        <CartesianGrid strokeDasharray="3 3" />
                                                        <XAxis type="number" />
                                                        <YAxis dataKey="name" type="category" width={100} />
                                                        <Tooltip content={<CustomTooltip />} />
                                                        <Bar
                                                            dataKey="avgPrice"
                                                            fill="#8b5cf6"
                                                            radius={[0, 8, 8, 0]}
                                                            name="평균 단가"
                                                        />
                                                    </BarChart>
                                                </ResponsiveContainer>
                                            </div>
                                        )}

                                        {/* 월별 평균 단가 추이 */}
                                        {monthlyPriceData.length > 0 && (
                                            <div className="chart-card">
                                                <div className="chart-header">
                                                    <h3>월별 평균 단가 추이</h3>
                                                </div>
                                                <ResponsiveContainer width="100%" height={300}>
                                                    <LineChart data={monthlyPriceData}>
                                                        <CartesianGrid strokeDasharray="3 3" />
                                                        <XAxis dataKey="month" />
                                                        <YAxis />
                                                        <Tooltip content={<CustomTooltip />} />
                                                        <Line
                                                            type="monotone"
                                                            dataKey="avgPrice"
                                                            stroke="#6366f1"
                                                            strokeWidth={3}
                                                            dot={{ r: 5 }}
                                                            name="평균 단가"
                                                        />
                                                    </LineChart>
                                                </ResponsiveContainer>
                                            </div>
                                        )}
                                    </div>
                                </>
                            )}
                        </>
                    )}
                </>
            )}
        </div>
    );
}

export default Statistics;
